// ═══════════════════════════════════════════════════════════════
//  MOTEUR D'INDEXATION DES LOYERS — FRANCE
//  Base légale : Indice de Référence des Loyers (IRL), INSEE
//  Loi n° 89-462 du 6 juillet 1989, art. 17-1
// ═══════════════════════════════════════════════════════════════

/**
 * Formule officielle (Service Public / INSEE) :
 *   Nouveau loyer = Loyer actuel × (IRL trimestre de référence N / IRL même trimestre N-1)
 *
 * Le trimestre de référence est celui inscrit dans la clause d'indexation du bail.
 * À défaut, c'est le dernier IRL publié à la date de signature du bail.
 *
 * Restrictions majeures (loi Climat et Résilience, art. 159) :
 *  - Logement classé F ou G au DPE → loyer GELÉ (révision interdite)
 *    Métropole : baux conclus/renouvelés depuis le 24/08/2022
 *    DROM : depuis le 01/07/2024
 *
 * Avertissements informatifs (non calculés) :
 *  - Zone tendue / encadrement des loyers : plafond préfectoral supplémentaire à vérifier
 *  - Majoration pour travaux d'amélioration : possible sous conditions contractuelles
 */

// Date d'entrée en vigueur du gel des passoires thermiques
const GEL_PASSOIRE_METROPOLE = new Date('2022-08-24')
const GEL_PASSOIRE_DROM = new Date('2024-07-01')

// Codes département des DROM (Guadeloupe 971, Martinique 972, Guyane 973, Réunion 974, Mayotte 976)
const DEPARTEMENTS_DROM = ['971', '972', '973', '974', '976']

/**
 * Récupère la valeur IRL d'un trimestre donné.
 * @param {Array} indices - lignes de la table indices_irl [{annee, trimestre, valeur}]
 * @param {number} annee
 * @param {number} trimestre (1-4)
 * @returns {number|null}
 */
function getIRL(indices, annee, trimestre) {
  const row = indices.find(i => i.annee === annee && i.trimestre === trimestre)
  return row ? Number(row.valeur) : null
}

/**
 * Détermine le trimestre d'une date (1-4).
 */
function trimestreDeDate(date) {
  return Math.floor(date.getMonth() / 3) + 1
}

/**
 * Détermine le trimestre de référence IRL à utiliser.
 * Priorité : trimestre stipulé au bail. Sinon, dernier IRL publié à la signature.
 * @param {object} bail - { irl_trimestre_ref, irl_annee_ref, date_signature }
 * @param {Array} indices
 * @returns {{annee, trimestre}|null}
 */
function trimestreReference(bail, indices) {
  // Cas 1 : trimestre explicitement défini dans le bail
  if (bail.irl_trimestre_ref && bail.irl_annee_ref) {
    return { annee: Number(bail.irl_annee_ref), trimestre: Number(bail.irl_trimestre_ref) }
  }
  // Cas 2 : dernier IRL publié à la date de signature
  // L'IRL d'un trimestre est publié ~mi-trimestre suivant. On prend le dernier trimestre
  // dont la valeur existe et qui précède la signature.
  if (!bail.date_signature) return null
  const sig = new Date(bail.date_signature)
  // On cherche le trimestre le plus récent disponible AVANT la signature
  // (en pratique : le trimestre précédent celui de la signature)
  let annee = sig.getFullYear()
  let trim = trimestreDeDate(sig) - 1
  if (trim === 0) { trim = 4; annee -= 1 }
  // Vérifier que cet indice existe, sinon reculer
  for (let i = 0; i < 8; i++) {
    if (getIRL(indices, annee, trim) != null) return { annee, trimestre: trim }
    trim -= 1
    if (trim === 0) { trim = 4; annee -= 1 }
  }
  return null
}

/**
 * Vérifie si le logement est une passoire thermique soumise au gel.
 * @param {object} bail - { dpe, departement, date_entree }
 * @returns {{gele:boolean, raison:string|null}}
 */
function estGelePassoire(bail) {
  const dpe = (bail.dpe || '').toUpperCase().trim()
  if (dpe !== 'F' && dpe !== 'G') return { gele: false, raison: null }

  // Déterminer la date seuil selon métropole ou DROM
  const dept = String(bail.departement || '').trim()
  const estDrom = DEPARTEMENTS_DROM.includes(dept)
  const dateSeuil = estDrom ? GEL_PASSOIRE_DROM : GEL_PASSOIRE_METROPOLE

  // Le gel s'applique aux baux conclus/renouvelés depuis la date seuil
  const dateBail = bail.date_entree ? new Date(bail.date_entree) : null
  if (!dateBail || dateBail >= dateSeuil) {
    return {
      gele: true,
      raison: `Logement classé ${dpe} au DPE (passoire thermique) : la révision du loyer est interdite ` +
        `(art. 159 loi Climat et Résilience). Des travaux de rénovation énergétique sont nécessaires ` +
        `pour sortir des classes F/G avant toute indexation.`,
    }
  }
  // Bail antérieur à la date seuil : le gel ne s'applique pas (cas rare)
  return { gele: false, raison: null }
}

/**
 * Calcule l'indexation IRL d'un loyer français.
 *
 * @param {object} params
 * @param {object} params.bail - {
 *     loyer_actuel, date_signature, date_entree, dpe, departement,
 *     irl_trimestre_ref, irl_annee_ref, zone_tendue (bool optionnel)
 *   }
 * @param {Array} params.indices - table indices_irl
 * @param {Date} params.dateRevision - date de la révision (défaut: aujourd'hui)
 * @returns {object} résultat détaillé
 */
function calculerIndexationFrance({ bail, indices, dateRevision = new Date() }) {
  const result = {
    ok: false,
    loyer_actuel: Number(bail.loyer_actuel),
    nouveau_loyer: null,
    irl_depart: null,
    irl_nouveau: null,
    trimestre_depart: null,
    trimestre_nouveau: null,
    augmentation: null,
    pourcentage: null,
    gele: false,
    avertissements: [],
    erreur: null,
  }

  if (!bail.loyer_actuel || Number(bail.loyer_actuel) <= 0) {
    result.erreur = 'Loyer actuel manquant ou invalide'
    return result
  }

  // 1) Gel passoire thermique (bloquant)
  const gel = estGelePassoire(bail)
  if (gel.gele) {
    result.gele = true
    result.nouveau_loyer = result.loyer_actuel  // inchangé
    result.augmentation = 0
    result.pourcentage = 0
    result.avertissements.push(gel.raison)
    result.ok = true  // calcul "réussi" mais loyer gelé
    return result
  }

  // 2) Trimestre de référence
  const trimRef = trimestreReference(bail, indices)
  if (!trimRef) {
    result.erreur = "Impossible de déterminer le trimestre de référence IRL (trimestre du bail ou date de signature manquants)"
    return result
  }

  // 3) IRL nouveau = trimestre de référence de l'année de révision
  //    IRL départ = même trimestre, année précédente
  const anneeRevision = dateRevision.getFullYear()
  const trimestre = trimRef.trimestre
  const irlNouveau = getIRL(indices, anneeRevision, trimestre)
  const irlDepart = getIRL(indices, anneeRevision - 1, trimestre)

  result.trimestre_nouveau = { annee: anneeRevision, trimestre }
  result.trimestre_depart = { annee: anneeRevision - 1, trimestre }

  if (irlNouveau == null) {
    result.erreur = `IRL introuvable pour ${anneeRevision}-T${trimestre} (pas encore publié ?)`
    return result
  }
  if (irlDepart == null) {
    result.erreur = `IRL de départ introuvable pour ${anneeRevision - 1}-T${trimestre}`
    return result
  }
  result.irl_nouveau = irlNouveau
  result.irl_depart = irlDepart

  // 4) Formule officielle
  const nouveauLoyer = Number(bail.loyer_actuel) * (irlNouveau / irlDepart)
  result.nouveau_loyer = Math.round(nouveauLoyer * 100) / 100
  result.augmentation = Math.round((result.nouveau_loyer - result.loyer_actuel) * 100) / 100
  result.pourcentage = Math.round(((irlNouveau / irlDepart - 1) * 100) * 100) / 100

  // 5) Cas IRL en baisse (rare) : le loyer ne peut pas être augmenté, mais pas d'obligation de baisse
  if (irlNouveau < irlDepart) {
    result.avertissements.push(
      "L'IRL de référence a baissé : aucune augmentation n'est possible cette année."
    )
  }

  // 6) Avertissement zone tendue / encadrement des loyers (informatif)
  if (bail.zone_tendue) {
    result.avertissements.push(
      "Logement en zone d'encadrement des loyers : le loyer révisé ne peut dépasser le loyer de " +
      "référence majoré fixé par arrêté préfectoral. Vérifiez le plafond applicable à votre commune " +
      "(le montant calculé ici ne tient pas compte de ce plafond local)."
    )
  }

  // 7) Avertissement majoration travaux (informatif)
  result.avertissements.push(
    "Rappel : une majoration pour travaux d'amélioration est possible au-delà de l'IRL, sous conditions " +
    "(clause au bail ou avenant signé, travaux ≥ la moitié de la dernière année de loyer). Interdite pour les DPE F/G."
  )

  result.ok = true
  return result
}

export {
  calculerIndexationFrance,
  getIRL,
  trimestreReference,
  estGelePassoire,
  trimestreDeDate,
}